import React, { useState, useEffect } from 'react';
import { 
  Building2, DollarSign, Calendar, AlertCircle, CheckCircle2,
  Clock, FileText, Download, Filter, TrendingUp, 
  CreditCard, Send, Eye, ChevronRight, RefreshCw,
  AlertTriangle, ArrowUpRight, ArrowDownRight, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Progress } from '../../ui/progress';
import { db } from '../../../lib/db';
import { formatUSDCurrency } from '../../../lib/usPayrollCalculations';
import { TAX_DEADLINES, STATE_NAMES } from '../../../lib/usPayrollData';
import { formatDate } from '../../../lib/dateUtils';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

const LIABILITY_STATUS = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  due: { label: 'Due', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  filed: { label: 'Filed', color: 'bg-blue-100 text-blue-700', icon: FileText }
};

const TAX_TYPES = {
  federal_withholding: 'Federal Income Tax',
  fica: 'FICA (SS + Medicare)',
  futa: 'Federal Unemployment (FUTA)',
  state_withholding: 'State Income Tax',
  sui: 'State Unemployment (SUI)',
  sdi: 'State Disability (SDI)',
  local_withholding: 'Local Tax'
};

export default function TaxLiabilityManagement() {
  const [loading, setLoading] = useState(true);
  const [liabilities, setLiabilities] = useState([]);
  const [payments, setPayments] = useState([]);
  const [filings, setFilings] = useState([]);
  const [taxAccounts, setTaxAccounts] = useState([]);

  const [filter, setFilter] = useState({
    status: 'all',
    jurisdiction: 'all',
    period: 'current_quarter'
  });

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedLiability, setSelectedLiability] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('eftps');

  // Summary statistics
  const [summary, setSummary] = useState({
    totalPending: 0,
    totalDue: 0,
    totalPaid: 0,
    upcomingDeadlines: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [liabilityData, paymentData, filingData, accountData] = await Promise.all([
        db.taxLiabilities.toArray(),
        db.taxPayments.toArray(),
        db.taxFilings.toArray(),
        db.taxAccounts.toArray()
      ]);

      // Calculate due status based on current date
      const now = Date.now();
      const updatedLiabilities = liabilityData.map(l => {
        if (l.status === 'pending' && l.dueDate < now) {
          return { ...l, status: 'due' };
        }
        return l;
      });

      setLiabilities(updatedLiabilities);
      setPayments(paymentData);
      setFilings(filingData);
      setTaxAccounts(accountData);

      // Calculate summary
      const pending = updatedLiabilities
        .filter(l => l.status === 'pending')
        .reduce((sum, l) => sum + l.amount, 0);
      const due = updatedLiabilities
        .filter(l => l.status === 'due')
        .reduce((sum, l) => sum + l.amount, 0);
      const paid = updatedLiabilities
        .filter(l => l.status === 'paid')
        .reduce((sum, l) => sum + l.amount, 0);

      // Get upcoming deadlines
      const upcoming = updatedLiabilities
        .filter(l => l.status === 'pending' || l.status === 'due')
        .sort((a, b) => a.dueDate - b.dueDate)
        .slice(0, 5);

      setSummary({
        totalPending: pending,
        totalDue: due,
        totalPaid: paid,
        upcomingDeadlines: upcoming
      });

    } catch (error) {
      console.error('Error loading tax data:', error);
      toast.error('Failed to load tax liability data');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredLiabilities = () => {
    let filtered = [...liabilities];

    if (filter.status !== 'all') {
      filtered = filtered.filter(l => l.status === filter.status);
    }

    if (filter.jurisdiction !== 'all') {
      filtered = filtered.filter(l => l.jurisdiction === filter.jurisdiction);
    }

    // Filter by period
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentQuarter = Math.floor(now.getMonth() / 3);

    if (filter.period === 'current_quarter') {
      const quarterStart = new Date(currentYear, currentQuarter * 3, 1);
      const quarterEnd = new Date(currentYear, (currentQuarter + 1) * 3, 0);
      filtered = filtered.filter(l => 
        l.createdAt >= quarterStart.getTime() && l.createdAt <= quarterEnd.getTime()
      );
    } else if (filter.period === 'current_month') {
      const monthStart = new Date(currentYear, now.getMonth(), 1);
      const monthEnd = new Date(currentYear, now.getMonth() + 1, 0);
      filtered = filtered.filter(l =>
        l.createdAt >= monthStart.getTime() && l.createdAt <= monthEnd.getTime()
      );
    }

    return filtered.sort((a, b) => a.dueDate - b.dueDate);
  };

  const recordPayment = async () => {
    if (!selectedLiability || !paymentAmount) {
      toast.error('Please enter a payment amount');
      return;
    }

    try {
      const amount = parseFloat(paymentAmount);
      
      // Create payment record
      await db.taxPayments.add({
        id: uuidv4(),
        taxLiabilityId: selectedLiability.id,
        amount,
        paymentDate: new Date(paymentDate).getTime(),
        paymentMethod,
        confirmationNumber: `PAY-${Date.now()}`,
        status: 'completed',
        createdAt: Date.now()
      });

      // Update liability status
      if (amount >= selectedLiability.amount) {
        await db.taxLiabilities.update(selectedLiability.id, {
          status: 'paid',
          paidAmount: amount,
          paidAt: Date.now()
        });
      } else {
        await db.taxLiabilities.update(selectedLiability.id, {
          paidAmount: (selectedLiability.paidAmount || 0) + amount
        });
      }

      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      setSelectedLiability(null);
      setPaymentAmount('');
      loadData();

    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error('Failed to record payment');
    }
  };

  const getDaysUntilDue = (dueDate) => {
    const days = Math.ceil((dueDate - Date.now()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Export tax liability report
  const exportReport = () => {
    const filtered = getFilteredLiabilities();
    
    if (filtered.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Tax Type', 'Jurisdiction', 'State', 'Period', 'Amount', 'Paid Amount', 'Due Date', 'Status'];
    const rows = filtered.map(l => [
      TAX_TYPES[l.taxType] || l.taxType,
      l.jurisdiction,
      l.state || 'N/A',
      formatDate(new Date(l.createdAt)),
      l.amount.toFixed(2),
      (l.paidAmount || 0).toFixed(2),
      formatDate(new Date(l.dueDate)),
      LIABILITY_STATUS[l.status]?.label || l.status
    ]);

    // Summary row
    const totalAmount = filtered.reduce((sum, l) => sum + l.amount, 0);
    const totalPaid = filtered.reduce((sum, l) => sum + (l.paidAmount || 0), 0);
    rows.push(['', '', '', 'TOTAL', totalAmount.toFixed(2), totalPaid.toFixed(2), '', '']);

    const csvContent = [
      'Tax Liability Report',
      `Generated: ${new Date().toLocaleString()}`,
      `Filter: ${filter.status === 'all' ? 'All Status' : filter.status}, ${filter.jurisdiction === 'all' ? 'All Jurisdictions' : filter.jurisdiction}`,
      '',
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tax-liabilities-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  // Open payment modal for making a general payment
  const openGeneralPayment = () => {
    const pendingLiabilities = liabilities.filter(l => l.status === 'pending' || l.status === 'due');
    if (pendingLiabilities.length === 0) {
      toast.info('No pending tax liabilities to pay');
      return;
    }
    // Select the most urgent (earliest due date)
    const mostUrgent = pendingLiabilities.sort((a, b) => a.dueDate - b.dueDate)[0];
    setSelectedLiability(mostUrgent);
    setPaymentAmount(String(mostUrgent.amount - (mostUrgent.paidAmount || 0)));
    setShowPaymentModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  const filteredLiabilities = getFilteredLiabilities();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tax Liability Management</h1>
          <p className="text-gray-500 mt-1">Track, pay, and file federal and state taxes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportReport}>
            <Download className="w-4 h-4 mr-2" /> Export Report
          </Button>
          <Button onClick={openGeneralPayment}>
            <CreditCard className="w-4 h-4 mr-2" /> Make Payment
          </Button>
        </div>
      </div>

      {/* Alert for Overdue */}
      {summary.totalDue > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Overdue Tax Liabilities</AlertTitle>
          <AlertDescription>
            You have {formatUSDCurrency(summary.totalDue)} in overdue tax payments. 
            Late payments may incur penalties and interest.
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-amber-600">{formatUSDCurrency(summary.totalPending)}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={summary.totalDue > 0 ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{formatUSDCurrency(summary.totalDue)}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Paid (YTD)</p>
                <p className="text-2xl font-bold text-emerald-600">{formatUSDCurrency(summary.totalPaid)}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total (YTD)</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatUSDCurrency(summary.totalPending + summary.totalDue + summary.totalPaid)}
                </p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="liabilities" className="space-y-4">
        <TabsList>
          <TabsTrigger value="liabilities">Tax Liabilities</TabsTrigger>
          <TabsTrigger value="payments">Payment History</TabsTrigger>
          <TabsTrigger value="deadlines">Upcoming Deadlines</TabsTrigger>
          <TabsTrigger value="filings">Tax Filings</TabsTrigger>
        </TabsList>

        <TabsContent value="liabilities">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Tax Liabilities</CardTitle>
                  <CardDescription>All accrued tax obligations from payroll</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select value={filter.status} onValueChange={(v) => setFilter({...filter, status: v})}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="due">Overdue</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filter.jurisdiction} onValueChange={(v) => setFilter({...filter, jurisdiction: v})}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Jurisdiction" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="federal">Federal</SelectItem>
                      <SelectItem value="state">State</SelectItem>
                      <SelectItem value="local">Local</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filter.period} onValueChange={(v) => setFilter({...filter, period: v})}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="current_month">Current Month</SelectItem>
                      <SelectItem value="current_quarter">Current Quarter</SelectItem>
                      <SelectItem value="ytd">Year to Date</SelectItem>
                      <SelectItem value="all">All Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tax Type</TableHead>
                    <TableHead>Jurisdiction</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLiabilities.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No tax liabilities found for the selected filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLiabilities.map(liability => {
                      const StatusIcon = LIABILITY_STATUS[liability.status]?.icon || Clock;
                      const daysUntilDue = getDaysUntilDue(liability.dueDate);

                      return (
                        <TableRow key={liability.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-gray-400" />
                              <span className="font-medium">
                                {TAX_TYPES[liability.taxType] || liability.taxType}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {liability.jurisdiction}
                              {liability.state && ` - ${STATE_NAMES[liability.state] || liability.state}`}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-500">
                            {formatDate(new Date(liability.createdAt))}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatUSDCurrency(liability.amount)}
                            {liability.paidAmount > 0 && liability.paidAmount < liability.amount && (
                              <div className="text-xs text-emerald-600">
                                Paid: {formatUSDCurrency(liability.paidAmount)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              {formatDate(new Date(liability.dueDate))}
                              {liability.status === 'pending' && daysUntilDue <= 7 && daysUntilDue > 0 && (
                                <Badge className="bg-amber-100 text-amber-700">
                                  {daysUntilDue} days
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={LIABILITY_STATUS[liability.status]?.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {LIABILITY_STATUS[liability.status]?.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {liability.status !== 'paid' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedLiability(liability);
                                  setPaymentAmount(String(liability.amount - (liability.paidAmount || 0)));
                                  setShowPaymentModal(true);
                                }}
                              >
                                <CreditCard className="w-4 h-4 mr-1" /> Pay
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle>Payment History</CardTitle>
              <CardDescription>Record of all tax payments made</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Tax Type</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Confirmation</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No payment history found
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map(payment => (
                      <TableRow key={payment.id}>
                        <TableCell>{formatDate(new Date(payment.paymentDate))}</TableCell>
                        <TableCell>
                          {(() => {
                            const liability = liabilities.find(l => l.id === payment.taxLiabilityId);
                            return TAX_TYPES[liability?.taxType] || 'Tax Payment';
                          })()}
                        </TableCell>
                        <TableCell className="capitalize">{payment.paymentMethod}</TableCell>
                        <TableCell className="text-right font-medium text-emerald-600">
                          {formatUSDCurrency(payment.amount)}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{payment.confirmationNumber}</TableCell>
                        <TableCell>
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deadlines">
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Tax Deadlines</CardTitle>
              <CardDescription>Key federal and state filing deadlines</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Federal Deadlines */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-blue-600" />
                    Federal Tax Deadlines
                  </h3>
                  <div className="grid gap-3">
                    {Object.entries(TAX_DEADLINES.federal).map(([key, deadline]) => (
                      <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{deadline.description}</p>
                          <p className="text-sm text-gray-500">Form {key.toUpperCase()}</p>
                        </div>
                        <Badge variant="outline">{deadline.deadline}</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* EFTPS Info */}
                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertTitle className="text-blue-800">EFTPS Payment System</AlertTitle>
                  <AlertDescription className="text-blue-700">
                    Federal tax deposits must be made through EFTPS (Electronic Federal Tax Payment System).
                    Deposits are typically due within 1-3 business days after payday, depending on your deposit schedule.
                  </AlertDescription>
                </Alert>

                {/* Liability Deadlines */}
                {summary.upcomingDeadlines.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-600" />
                      Your Upcoming Payments
                    </h3>
                    <div className="space-y-2">
                      {summary.upcomingDeadlines.map(liability => {
                        const daysUntilDue = getDaysUntilDue(liability.dueDate);
                        return (
                          <div 
                            key={liability.id} 
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              daysUntilDue < 0 ? 'bg-red-50' : 
                              daysUntilDue <= 7 ? 'bg-amber-50' : 'bg-gray-50'
                            }`}
                          >
                            <div>
                              <p className="font-medium">
                                {TAX_TYPES[liability.taxType]} - {liability.jurisdiction}
                              </p>
                              <p className="text-sm text-gray-500">
                                Due: {formatDate(new Date(liability.dueDate))}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">{formatUSDCurrency(liability.amount)}</p>
                              <p className={`text-sm ${
                                daysUntilDue < 0 ? 'text-red-600' :
                                daysUntilDue <= 7 ? 'text-amber-600' : 'text-gray-500'
                              }`}>
                                {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)} days overdue` :
                                 daysUntilDue === 0 ? 'Due today' :
                                 `${daysUntilDue} days remaining`}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="filings">
          <Card>
            <CardHeader>
              <CardTitle>Tax Filings</CardTitle>
              <CardDescription>Quarterly and annual tax form filings</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Form</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Filing Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        No tax filings recorded yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    filings.map(filing => (
                      <TableRow key={filing.id}>
                        <TableCell className="font-medium">{filing.formType}</TableCell>
                        <TableCell>{filing.period}</TableCell>
                        <TableCell>{formatDate(new Date(filing.filedAt))}</TableCell>
                        <TableCell>
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Filed
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost">
                            <Download className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Required Forms Section */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-3">Required Federal Forms</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="font-medium">Form 941</p>
                      <p className="text-gray-500">Quarterly federal tax return (withholding + FICA)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="font-medium">Form 940</p>
                      <p className="text-gray-500">Annual federal unemployment (FUTA) tax return</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="font-medium">Form W-2</p>
                      <p className="text-gray-500">Annual wage and tax statement (per employee)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="font-medium">Form W-3</p>
                      <p className="text-gray-500">Transmittal of W-2s to SSA</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Tax Payment</DialogTitle>
            <DialogDescription>
              Record a payment for {TAX_TYPES[selectedLiability?.taxType]}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between mb-2">
                <span className="text-gray-500">Liability Amount:</span>
                <span className="font-medium">{formatUSDCurrency(selectedLiability?.amount || 0)}</span>
              </div>
              {selectedLiability?.paidAmount > 0 && (
                <div className="flex justify-between mb-2">
                  <span className="text-gray-500">Already Paid:</span>
                  <span className="text-emerald-600">{formatUSDCurrency(selectedLiability.paidAmount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t">
                <span className="font-medium">Balance Due:</span>
                <span className="font-bold">
                  {formatUSDCurrency((selectedLiability?.amount || 0) - (selectedLiability?.paidAmount || 0))}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Payment Amount</Label>
              <Input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="eftps">EFTPS (Electronic)</SelectItem>
                  <SelectItem value="ach">ACH Transfer</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="wire">Wire Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
              Cancel
            </Button>
            <Button onClick={recordPayment}>
              <CheckCircle2 className="w-4 h-4 mr-2" /> Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
